import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

export interface SelectOption {
    value: string;
    label: string;
}

export interface SelectFieldProps {
    name: string;
    label?: string;
    options: SelectOption[];
    placeholder?: string;
    isMulti?: boolean;
    isDisabled?: boolean;
    required?: boolean;
    onAfterChange?: (value: string | string[]) => void;
}

export function SelectField({
    name,
    label,
    options,
    placeholder,
    isMulti,
    isDisabled,
    required,
    onAfterChange,
}: SelectFieldProps) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const { t } = useTranslation();
    const error = errors[name];
    const message = typeof error?.message === 'string' ? t(error.message) : undefined;
    const errorId = `${name}-error`;

    return (
        <Form.Group className="mb-3" controlId={name}>
            {label && (
                <Form.Label htmlFor={name}>
                    {label}
                    {required && (
                        <span className="field-required-mark" aria-hidden="true">
                            *
                        </span>
                    )}
                </Form.Label>
            )}
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <Select<SelectOption, boolean>
                        inputId={name}
                        options={options}
                        isMulti={isMulti}
                        isDisabled={isDisabled}
                        placeholder={placeholder ?? t('common.selectPlaceholder')}
                        noOptionsMessage={() => t('common.noOptions')}
                        classNamePrefix="dkn-rs"
                        className={error ? 'is-invalid' : undefined}
                        aria-invalid={!!error}
                        aria-errormessage={error ? errorId : undefined}
                        value={
                            isMulti
                                ? options.filter(
                                      (o) => Array.isArray(field.value) && field.value.includes(o.value),
                                  )
                                : (options.find((o) => o.value === field.value) ?? null)
                        }
                        onBlur={field.onBlur}
                        onChange={(val) => {
                            if (isMulti) {
                                const next = (val as SelectOption[]).map((v) => v.value);
                                field.onChange(next);
                                onAfterChange?.(next);
                            } else {
                                const next = (val as SelectOption | null)?.value ?? '';
                                field.onChange(next);
                                onAfterChange?.(next);
                            }
                        }}
                    />
                )}
            />
            {message && (
                <div className="invalid-feedback d-block" id={errorId}>
                    {message}
                </div>
            )}
        </Form.Group>
    );
}
